import {useCallback, useContext, useEffect, useState, ReactNode} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {captureException, captureMessage} from '@sentry/browser';
import {css, Spinner, view} from '@sail/ui/deprecated';
import {Form as RACForm} from '@stripe-internal/conversations-ui';
import {ConfigContext} from '../../../Providers/ConfigProvider';
import {
  EligibleChannelsEndpointResponseChannelRequestACall,
  RequestACallEndpointResponseClientError,
} from '../../../../generated/types';
import {countryForLocale} from '../../../helpers/intl';
import {useEligibleChannels} from '../../ChannelSelector';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {useProfile} from '../../../Providers/ProfileProvider';
import {useApi} from '../../../Providers/ApiProvider/ApiProvider';

export const messages = defineMessages({
  genericErrorMessage: {
    id: 'support_conversations_client.rac.form.genericErrorMessage',
    description: 'Error message indicating a generic stripe error',
    defaultMessage: 'Something went wrong. Please try again.',
  },
  fieldErrorMessage: {
    id: 'support_conversations_client.rac.form.fieldErrorMessage',
    description:
      'Error message indicating there is an issue with one of the form field values',
    defaultMessage: 'All fields are required.',
  },
  rateLimitedErrorMessage: {
    id: 'support_conversations_client.rac.form.rateLimitedErrorMessage',
    description:
      'Error message indicating the user is rate limited from Request A Call',
    defaultMessage:
      'You have submitted too many requests. Please wait and try again later.',
  },
  countryCodeErrorMessage: {
    id: 'support_conversations_client.rac.form.countryCodeErrorMessage',
    description:
      "Error message indicating the user's country code is unsupported from Request A Call",
    defaultMessage:
      'The country code is unsupported. Please use a different country code or contact method.',
  },
});

const renderClientError = (
  clientError?: RequestACallEndpointResponseClientError['type'],
): ReactNode => {
  switch (clientError) {
    case 'unauthorized':
    case 'ineligible':
      return <FormattedMessage {...messages.genericErrorMessage} />;
    case 'invalid-argument':
      return <FormattedMessage {...messages.fieldErrorMessage} />;
    case 'rate-limit-exceeded':
      return <FormattedMessage {...messages.rateLimitedErrorMessage} />;
    default:
      return null;
  }
};

export const RequestACallForm = ({
  topic,
  audience,
  entryPoint,
  conversationId,
  initialMessage,
  onSuccess,
  onCancel,
}: {
  topic: string;
  audience?: string;
  entryPoint?: string;
  conversationId?: string;
  initialMessage?: string;
  onSuccess: ({name, phoneNumber}: {name: string; phoneNumber: string}) => void;
  onCancel: () => void;
}) => {
  const {audience: configAudience, entrypoint: configEntryPoint} =
    useContext(ConfigContext);
  const {profile} = useProfile();
  const {visitor} = profile;
  const {locale} = useIntl();
  const country = countryForLocale(locale);

  const {requestACall} = useApi();

  const {eligibleChannels} = useEligibleChannels();
  const [channel, setChannel] = useState<
    EligibleChannelsEndpointResponseChannelRequestACall | undefined
  >();

  const [serverError, setServerError] = useState<boolean>(false);
  const [clientError, setClientError] =
    useState<RequestACallEndpointResponseClientError['type']>();

  const analytics = useClientAnalytics();

  useEffect(() => {
    setChannel(
      eligibleChannels?.find((c) => {
        return c.type === 'request_a_call';
      }) as EligibleChannelsEndpointResponseChannelRequestACall | undefined,
    );
  }, [eligibleChannels]);

  const handleCancel = useCallback(() => {
    analytics.action('support_conversations_rac_cancel');
    onCancel();
  }, [onCancel, analytics]);

  const handleSubmit = useCallback(
    async (name: string, phoneNumber: string, message: string) => {
      analytics.action('support_conversations_rac_submit');

      const analyticsEventId = await analytics.requestAnalyticsEventId();

      try {
        const response = await requestACall({
          phoneNumber,
          message,
          name,
          topic,
          entryPoint: entryPoint ?? configEntryPoint,
          audience: audience ?? configAudience,
          analyticsEventId,
          universalHelpConversationId: conversationId,
        });

        if (response.type === 'success') {
          analytics.track('support_conversations_rac_success');
          onSuccess({name, phoneNumber});
        } else {
          analytics.track('support_conversations_rac_client_error', {
            universalHelp: true,
            topic,
          });
          setClientError(response.type);
          captureMessage('RAC submission error', {
            level: 'warning',
            extra: {
              responseType: response.type,
              audience,
              entryPoint,
              topic,
            },
          });
        }
      } catch (e) {
        captureException(e);
        analytics.track('support_conversations_rac_server_error');
        setServerError(true);
      }
    },
    [
      analytics,
      audience,
      configAudience,
      configEntryPoint,
      conversationId,
      entryPoint,
      onSuccess,
      topic,
      requestACall,
    ],
  );

  if (!channel) {
    return (
      <view.div
        data-testid="loading-spinner"
        css={{
          width: 'fill',
          marginTop: 'medium',
          height: css.value(`${window.innerHeight / 3}px`),
          stack: 'y',
          alignX: 'center',
          alignY: 'center',
        }}
      >
        <Spinner />
      </view.div>
    );
  }

  return (
    <div data-testid="request-a-call-form-screen">
      <RACForm
        initialName={visitor.displayName || undefined}
        initialPhoneNumber={visitor.phoneNumber || undefined}
        initialMessage={initialMessage}
        defaultCountry={country || undefined}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        clientError={
          serverError ? (
            <FormattedMessage {...messages.genericErrorMessage} />
          ) : (
            renderClientError(clientError)
          )
        }
      />
    </div>
  );
};
